import { PictureOutlined } from '@ant-design/icons';
import { Upload } from 'antd';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import React, { useCallback } from 'react';
import useAppMessage from '@/hooks/useAppMessage';
import { uploadObjectFile } from '@/services/storage/upload';

const minioBaseUrl =
  process.env.NODE_ENV === 'development'
    ? 'https://ai.xzinfra.com'
    : typeof window !== 'undefined'
    ? window.location.origin
    : '';

/** 拼接 MinIO 访问地址：{host}/minio/{path} */
export function getMinioUrl(url: string) {
  return `${minioBaseUrl}/minio/${url}`;
}

/** 将 storage 对象 url 转为可访问的图片地址 */
export function getObjectFileUrl(url?: string | null): string | undefined {
  const value = url?.trim();
  if (!value) {
    return undefined;
  }
  return getMinioUrl(value.replace(/^\//, ''));
}

export const objectUploadNormFile = (
  event: { fileList?: UploadFile[] } | UploadFile[],
) => {
  if (Array.isArray(event)) {
    return event;
  }
  return event?.fileList || [];
};

export function getObjectUploadFileId(
  fileList?: UploadFile[] | null,
): string | undefined {
  const file = fileList?.[0];
  const fileId = (file?.response as { fileId?: string } | undefined)?.fileId;
  return fileId?.trim() || undefined;
}

export interface ObjectUploadProps extends Omit<UploadProps, 'customRequest'> {
  uploadText?: React.ReactNode;
}

const ObjectUpload: React.FC<ObjectUploadProps> = ({
  accept = '.png,.jpg,.jpeg,.ico,.svg',
  listType = 'picture-card',
  maxCount = 1,
  multiple = false,
  uploadText = '选择文件',
  children,
  ...uploadProps
}) => {
  const appMessage = useAppMessage();

  const handleCustomUpload = useCallback<
    NonNullable<UploadProps['customRequest']>
  >(
    async ({ file, onError, onProgress, onSuccess }) => {
      await uploadObjectFile({
        file: file as File,
        onError: (error) => {
          onError?.(error);
          appMessage.error(error.message || '上传失败，请重新选择文件');
        },
        onProgress: (progress) => {
          onProgress?.(progress);
        },
        onSuccess: (data, uploadedFile) => {
          onSuccess?.(data, uploadedFile);
        },
      });
    },
    [appMessage],
  );

  return (
    <Upload
      accept={accept}
      listType={listType}
      maxCount={maxCount}
      multiple={multiple}
      customRequest={handleCustomUpload}
      {...uploadProps}
    >
      {children ?? (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <PictureOutlined style={{ fontSize: 20 }} />
          <div style={{ marginTop: 8 }}>{uploadText}</div>
        </div>
      )}
    </Upload>
  );
};

export default ObjectUpload;
